import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/formfields";
import type {
  CertificationDetail,
  CertificationPayload,
} from "@/interface/certification";
import {
  useCreateCertification,
  useUpdateCertification,
} from "../hooks/useCertifications";
import { useProfileDetail } from "../hooks/useProfile";

const getTodayDateString = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const certSchema = z
  .object({
    certification_name: z
      .string()
      .trim()
      .min(1, "Certification Name is required")
      .max(255, "Certification Name cannot exceed 255 characters"),
    issuing_organization: z
      .string()
      .trim()
      .min(1, "Issuing Organization is required")
      .max(255, "Issuing Organization cannot exceed 255 characters"),
    issue_date: z.string().trim().min(1, "Issue Date is required"),
    expiry_date: z.string().trim().optional(),
    credential_id: z.string().trim().optional(),
    credential_url: z.string().trim().optional(),
  })
  .superRefine((data, ctx) => {
    const todayStr = getTodayDateString();

    if (data.issue_date && data.issue_date > todayStr) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Issue date cannot be in the future",
        path: ["issue_date"],
      });
    }

    if (data.issue_date && data.expiry_date && data.expiry_date.trim()) {
      const issue = new Date(data.issue_date);
      const expiry = new Date(data.expiry_date);

      if (
        !isNaN(issue.getTime()) &&
        !isNaN(expiry.getTime()) &&
        issue > expiry
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Issue date must be before expiry date",
          path: ["expiry_date"],
        });
      }
    }
  });

export type CertificationFormValues = z.infer<typeof certSchema>;

interface CertificationFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  editData?: CertificationDetail | null;
  profileId?: number | string;
}

export const CertificationFormModal: React.FC<CertificationFormModalProps> = ({
  isOpen,
  onClose,
  editData,
  profileId,
}) => {
  const createMutation = useCreateCertification();
  const updateMutation = useUpdateCertification();
  const { data: profileResponse } = useProfileDetail();

  const selfId = profileResponse?.data?.id;
  const targetUserId = profileId ?? selfId ?? editData?.user_profile;

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<CertificationFormValues>({
    resolver: zodResolver(certSchema),
    defaultValues: {
      certification_name: "",
      issuing_organization: "",
      issue_date: "",
      expiry_date: "",
      credential_id: "",
      credential_url: "",
    },
  });

  useEffect(() => {
    if (isOpen) {
      if (editData) {
        reset({
          certification_name: editData.certification_name || "",
          issuing_organization: editData.issuing_organization || "",
          issue_date: editData.issue_date || "",
          expiry_date: editData.expiry_date || "",
          credential_id: editData.credential_id || "",
          credential_url: editData.credential_url || "",
        });
      } else {
        reset({
          certification_name: "",
          issuing_organization: "",
          issue_date: "",
          expiry_date: "",
          credential_id: "",
          credential_url: "",
        });
      }
    }
  }, [isOpen, editData, reset]);

  const onSubmit = async (values: CertificationFormValues) => {
    try {
      const payload: CertificationPayload = {
        certification_name: values.certification_name,
        issuing_organization: values.issuing_organization,
        issue_date: values.issue_date,
        expiry_date: values.expiry_date || null,
        credential_id: values.credential_id || null,
        credential_url: values.credential_url || null,
        user_profile: targetUserId,
      };

      if (editData) {
        await updateMutation.mutateAsync({ id: editData.id, payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      onClose();
    } catch {
      // Handled by query mutation
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0">
        <DialogHeader className="p-6 pb-4 border-b border-slate-100 dark:border-slate-800">
          <DialogTitle>
            {editData ? "Edit Certification" : "Add Certification"}
          </DialogTitle>
        </DialogHeader>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col flex-1 overflow-hidden"
        >
          <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-slate-50/50 dark:bg-slate-900/50">
            <TextInput
              control={control}
              name="certification_name"
              label="Certification Name"
              placeholder="e.g. AWS Certified Solutions Architect"
              required
            />

            <TextInput
              control={control}
              name="issuing_organization"
              label="Issuing Organization"
              placeholder="e.g. Amazon Web Services"
              required
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <TextInput
                control={control}
                name="issue_date"
                label="Issue Date"
                type="date"
                max={getTodayDateString()}
                required
              />
              <TextInput
                control={control}
                name="expiry_date"
                label="Expiry Date"
                type="date"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <TextInput
                control={control}
                name="credential_id"
                label="Credential ID"
                placeholder="e.g. AWS-123456"
              />
              <TextInput
                control={control}
                name="credential_url"
                label="Credential URL"
                placeholder="https://..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                isSubmitting ||
                createMutation.isPending ||
                updateMutation.isPending
              }
            >
              {isSubmitting ||
              createMutation.isPending ||
              updateMutation.isPending
                ? "Saving..."
                : editData
                  ? "Save Changes"
                  : "Add Certification"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CertificationFormModal;
